import type { DashaPeriod, Planet, VimshottariResult } from '@astrally/shared';
import { norm360 } from '../math/angles.js';
import { NAKSHATRA_SPAN, nakshatraOf } from '../core/zodiac.js';

/**
 * Vimshottari dasha. 120-year cycle keyed to the Moon's nakshatra at birth.
 * Year length convention: 365.25 days by default (configurable; some
 * traditions use 360-day years — recorded in the result for transparency).
 */

export const DASHA_SEQUENCE: { lord: Planet; years: number }[] = [
  { lord: 'ketu', years: 7 },
  { lord: 'venus', years: 20 },
  { lord: 'sun', years: 6 },
  { lord: 'moon', years: 10 },
  { lord: 'mars', years: 7 },
  { lord: 'rahu', years: 18 },
  { lord: 'jupiter', years: 16 },
  { lord: 'saturn', years: 19 },
  { lord: 'mercury', years: 17 },
];

const TOTAL_YEARS = 120;

function lordIndex(lord: Planet): number {
  const i = DASHA_SEQUENCE.findIndex((d) => d.lord === lord);
  if (i < 0) throw new Error(`Not a dasha lord: ${lord}`);
  return i;
}

export function computeVimshottari(
  moonLonSidereal: number,
  birthUtc: Date,
  yearDays = 365.25,
  levels: 1 | 2 | 3 = 3,
): VimshottariResult {
  const lon = norm360(moonLonSidereal);
  const nak = nakshatraOf(lon);
  const withinNak = lon - nak.index * NAKSHATRA_SPAN;
  const elapsedFraction = withinNak / NAKSHATRA_SPAN;

  const startIdx = lordIndex(nak.lord);
  const firstYears = DASHA_SEQUENCE[startIdx].years;
  const balanceYears = firstYears * (1 - elapsedFraction);

  const msPerYear = yearDays * 86400000;
  // The birth mahadasha began before birth by the elapsed fraction.
  let cursor = birthUtc.getTime() - firstYears * elapsedFraction * msPerYear;

  const periods: DashaPeriod[] = [];
  let accumulated = 0;
  for (let i = 0; accumulated < TOTAL_YEARS; i++) {
    const { lord, years } = DASHA_SEQUENCE[(startIdx + i) % 9];
    const start = cursor;
    const end = start + years * msPerYear;
    const maha: DashaPeriod = {
      lord,
      level: 1,
      startISO: new Date(start).toISOString(),
      endISO: new Date(end).toISOString(),
    };
    if (levels >= 2) {
      maha.children = subPeriods(lord, start, years, msPerYear, 2, levels);
    }
    periods.push(maha);
    cursor = end;
    accumulated += years;
  }

  return {
    moonLonSidereal: lon,
    moonNakshatra: nak,
    balanceYears,
    periods,
    yearDays,
  };
}

function subPeriods(
  parentLord: Planet,
  parentStartMs: number,
  parentYears: number,
  msPerYear: number,
  level: 2 | 3,
  maxLevel: 1 | 2 | 3,
): DashaPeriod[] {
  const out: DashaPeriod[] = [];
  const startIdx = lordIndex(parentLord);
  let cursor = parentStartMs;
  for (let i = 0; i < 9; i++) {
    const { lord, years } = DASHA_SEQUENCE[(startIdx + i) % 9];
    const lengthYears = (parentYears * years) / TOTAL_YEARS;
    const start = cursor;
    const end = start + lengthYears * msPerYear;
    const period: DashaPeriod = {
      lord,
      level,
      startISO: new Date(start).toISOString(),
      endISO: new Date(end).toISOString(),
    };
    if (level < maxLevel) {
      period.children = subPeriods(lord, start, lengthYears, msPerYear, 3, maxLevel);
    }
    out.push(period);
    cursor = end;
  }
  return out;
}

/** Find the running periods (maha, antar, pratyantar) at a given instant. */
export function activePeriods(result: VimshottariResult, at: Date): DashaPeriod[] {
  const t = at.getTime();
  const chain: DashaPeriod[] = [];
  let scope: DashaPeriod[] | undefined = result.periods;
  while (scope) {
    const hit: DashaPeriod | undefined = scope.find(
      (p) => t >= Date.parse(p.startISO) && t < Date.parse(p.endISO),
    );
    if (!hit) break;
    chain.push(hit);
    scope = hit.children;
  }
  return chain;
}
